import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Descriptions, Form, Grid, Modal, Radio, Select, Space, Typography } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createPrescriptionDraft, fetchPrescriptionDraft, updatePrescriptionDraft } from "../../../../../api/healthcare/PrescriptionDraftApi";
import { runKey as newClientRequestId } from "../../../../../api/healthcare/Consultation/ConsultGenerationApi";
import { useNotifications } from "../../../../../context/NotificationContext";
import type { PrescribingOrganisationsState } from "../../../../../hooks/healthcare/usePrescribingOrganisations";
import type { Patient } from "../../../../../types/healthcare/PatientTypes";
import {
	PRESCRIPTION_MODALITIES,
	PRESCRIPTION_PARTICULAR_KEYS,
	PRESCRIPTION_PARTICULAR_RULES,
	type PrescriptionDraft,
	type PrescriptionModality,
	type PrescriptionParticularKey,
	type PrescriptionParticulars,
	type UpdatePrescriptionDraftRequest,
} from "../../../../../types/healthcare/PrescriptionDraftTypes";
import { getSessionUser } from "../../../../../utils/auth/sessionUser";
import {
	DRAFT_NOT_ISSUED_LABEL,
	changedParticulars,
	isEditableDraft,
	modalityLabel,
	normaliseParticulars,
	sameDraftContent,
	shortReference,
} from "../../../../../utils/healthcare/prescriptionDraftDisplay";
import { classifyPrescriptionDraftFailure, type PrescriptionDraftFailure } from "../../../../../utils/healthcare/prescriptionDraftFailure";
import { canWritePrescriptionDrafts } from "../../../../../utils/userPrefs/validatePermissions";
import { formatInZone } from "../../../../../utils/time";
import StatusTag from "../../../../../components/shared/StatusTag";
import PrescriptionParticularsFields from "./PrescriptionParticularsFields";

const { Text } = Typography;

export type PrescriptionDraftModalMode = { kind: "create" } | { kind: "view"; uuid: string } | { kind: "edit"; uuid: string };

interface PrescriptionDraftModalProps {
	patient: Patient;
	initialMode: PrescriptionDraftModalMode;
	initialDraft?: PrescriptionDraft;
	organisations: PrescribingOrganisationsState;
	onClose: () => void;
}

interface DraftFormValues {
	modality?: PrescriptionModality;
	organisation_uuid?: string;
	particulars?: Partial<Record<keyof PrescriptionParticulars, unknown>>;
}

type Intended = { modality: PrescriptionModality; organisation_uuid: string; particulars: PrescriptionParticulars };

export const prescriptionDraftsKey = (patientUuid: string) => ["prescriptionDrafts", patientUuid] as const;
const draftKey = (uuid: string) => ["prescriptionDraft", uuid] as const;

const FORM_FIELDS = new Set(["modality", "organisation_uuid", ...PRESCRIPTION_PARTICULAR_KEYS.map((key) => `particulars.${key}`)]);

const formatInstant = (iso: string | null): string => (iso ? formatInZone(iso, "D MMM YYYY, h:mm a") || "—" : "—");

const isLongParticular = (key: PrescriptionParticularKey): boolean => {
	const rule = PRESCRIPTION_PARTICULAR_RULES[key];
	return rule.kind === "text" && rule.max > 255;
};

const PrescriptionDraftModal: React.FC<PrescriptionDraftModalProps> = ({ patient, initialMode, initialDraft, organisations, onClose }) => {
	const [mode, setMode] = useState<PrescriptionDraftModalMode>(initialMode);
	const [editBase, setEditBase] = useState<PrescriptionDraft | null>(null);
	const [failure, setFailure] = useState<PrescriptionDraftFailure | null>(null);
	const [statusText, setStatusText] = useState("");
	const [form] = Form.useForm<DraftFormValues>();
	const queryClient = useQueryClient();
	const { showSuccess, showInfo } = useNotifications();
	const screens = Grid.useBreakpoint();
	const requestIdRef = useRef<string>(newClientRequestId());
	const lastEditAmbiguousRef = useRef(false);

	const session = getSessionUser();
	const sessionUuid = session?.uuid ?? "";
	const sessionName = [session?.first_name, session?.last_name].filter(Boolean).join(" ") || session?.username || "the signed-in user";
	const uuid = mode.kind === "create" ? null : mode.uuid;

	const draftQuery = useQuery({
		queryKey: draftKey(uuid ?? ""),
		queryFn: () => fetchPrescriptionDraft(uuid as string),
		enabled: uuid !== null,
		initialData: initialDraft && initialDraft.uuid === uuid ? initialDraft : undefined,
		refetchOnWindowFocus: false,
		retry: false,
	});
	const draft = uuid ? draftQuery.data : undefined;

	const wrongPatient = !!draft && draft.patient_uuid !== patient.uuid;
	const ownDraft = !!draft && draft.prescriber_user_uuid === sessionUuid;
	const mayEdit = !!draft && !wrongPatient && ownDraft && isEditableDraft(draft) && canWritePrescriptionDrafts();
	const organisationOptions = organisations.status === "ready" ? organisations.organisations : [];
	const organisationLabel = (orgUuid: string) => organisationOptions.find((org) => org.uuid === orgUuid)?.label ?? `Organisation reference ${shortReference(orgUuid)}`;

	const createMutation = useMutation({ mutationFn: (payload: Parameters<typeof createPrescriptionDraft>[1]) => createPrescriptionDraft(patient.uuid, payload) });
	const updateMutation = useMutation({ mutationFn: ({ id, payload }: { id: string; payload: UpdatePrescriptionDraftRequest }) => updatePrescriptionDraft(id, payload) });
	const saving = createMutation.isPending || updateMutation.isPending;

	const loadIntoForm = (source: PrescriptionDraft) => {
		form.resetFields();
		form.setFieldsValue({ modality: source.modality, organisation_uuid: source.organisation_uuid, particulars: { ...source.particulars } });
	};

	useEffect(() => {
		if (mode.kind === "edit" && draft && !editBase) {
			setEditBase(draft);
			loadIntoForm(draft);
		}
	}, [mode.kind, draft, editBase]);

	const adoptSaved = (saved: PrescriptionDraft) => {
		queryClient.setQueryData(draftKey(saved.uuid), saved);
		queryClient.invalidateQueries({ queryKey: prescriptionDraftsKey(patient.uuid) });
		lastEditAmbiguousRef.current = false;
		setFailure(null);
		setEditBase(null);
		setStatusText(`Saved as revision ${saved.revision}. Still a draft — not issued.`);
		setMode({ kind: "view", uuid: saved.uuid });
	};

	const applyFailure = (next: PrescriptionDraftFailure) => {
		if (next.kind === "stepup_declined") return;
		if (next.kind === "validation") {
			form.setFields(Object.entries(next.fields).filter(([name]) => FORM_FIELDS.has(name)).map(([name, message]) => ({ name: name.split("."), errors: [message] })));
		}
		if (next.kind === "request_id_reused") queryClient.invalidateQueries({ queryKey: prescriptionDraftsKey(patient.uuid) });
		setStatusText("");
		setFailure(next);
	};

	const saveCreate = async (values: DraftFormValues) => {
		const payload = {
			client_request_id: requestIdRef.current,
			organisation_uuid: values.organisation_uuid as string,
			modality: values.modality as PrescriptionModality,
			particulars: normaliseParticulars(values.particulars),
		};
		try {
			const { draft: saved, created } = await createMutation.mutateAsync(payload);
			if (created) showSuccess("Prescription draft saved. It has not been approved, issued or sent.");
			else showInfo("Your earlier attempt had already saved this draft, so no duplicate was created.");
			adoptSaved(saved);
		} catch (error) {
			applyFailure(classifyPrescriptionDraftFailure(error));
		}
	};

	const saveEdit = async (base: PrescriptionDraft, values: DraftFormValues) => {
		const intended: Intended = {
			modality: values.modality ?? base.modality,
			organisation_uuid: values.organisation_uuid ?? base.organisation_uuid,
			particulars: normaliseParticulars(values.particulars),
		};
		const changes = changedParticulars(normaliseParticulars(base.particulars), intended.particulars);
		const payload: UpdatePrescriptionDraftRequest = { revision: base.revision };
		if (intended.modality !== base.modality) payload.modality = intended.modality;
		if (intended.organisation_uuid !== base.organisation_uuid) payload.organisation_uuid = intended.organisation_uuid;
		if (Object.keys(changes).length > 0) payload.particulars = changes;

		if (Object.keys(payload).length === 1) {
			showInfo("There are no changes to save.");
			setEditBase(null);
			setMode({ kind: "view", uuid: base.uuid });
			return;
		}

		try {
			const saved = await updateMutation.mutateAsync({ id: base.uuid, payload });
			showSuccess("Prescription draft updated. It has not been approved, issued or sent.");
			adoptSaved(saved);
		} catch (error) {
			const next = classifyPrescriptionDraftFailure(error);
			const retryingAmbiguous = lastEditAmbiguousRef.current;
			lastEditAmbiguousRef.current = next.kind === "ambiguous";
			if (next.kind === "stale_revision" && retryingAmbiguous) {
				// CU-14ygtkjeh2z: a retried PATCH whose first attempt landed sees its own revision bump; success only if the server holds exactly these values.
				const latest = await queryClient.fetchQuery({ queryKey: draftKey(base.uuid), queryFn: () => fetchPrescriptionDraft(base.uuid) }).catch(() => null);
				if (latest && sameDraftContent(latest, intended)) {
					showSuccess("Your earlier save was applied. The draft is up to date.");
					adoptSaved(latest);
					return;
				}
			}
			applyFailure(next);
		}
	};

	const handleSave = async () => {
		let values: DraftFormValues;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}
		setFailure(null);
		setStatusText("Saving draft…");
		if (mode.kind === "create") await saveCreate(values);
		else if (editBase) await saveEdit(editBase, values);
	};

	const loadLatest = async () => {
		if (!uuid) return;
		setFailure(null);
		try {
			const latest = await queryClient.fetchQuery({ queryKey: draftKey(uuid), queryFn: () => fetchPrescriptionDraft(uuid) });
			lastEditAmbiguousRef.current = false;
			setEditBase(latest);
			loadIntoForm(latest);
			setStatusText(`Loaded revision ${latest.revision}. Your unsaved changes were discarded.`);
		} catch (error) {
			applyFailure(classifyPrescriptionDraftFailure(error));
		}
	};

	const confirmDiscard = (discard: () => void) => {
		if ((mode.kind === "create" || mode.kind === "edit") && form.isFieldsTouched()) {
			Modal.confirm({
				title: "Discard unsaved changes?",
				content: "Your changes to this prescription draft have not been saved.",
				okText: "Discard",
				okType: "danger",
				cancelText: "Keep editing",
				onOk: discard,
			});
			return;
		}
		discard();
	};

	const requestClose = () => confirmDiscard(onClose);

	const startEdit = () => {
		setFailure(null);
		setStatusText("");
		setEditBase(null);
		if (uuid) setMode({ kind: "edit", uuid });
	};

	const cancelEdit = () =>
		confirmDiscard(() => {
			setFailure(null);
			setEditBase(null);
			form.resetFields();
			if (uuid) setMode({ kind: "view", uuid });
		});

	const creating = mode.kind === "create";
	const editing = mode.kind === "edit";
	const organisationsReady = organisations.status === "ready" && organisationOptions.length > 0;
	// CU-14ygtkjeh2z: once Healthcare holds a draft under this request id, a fresh id would duplicate it, so this form stops creating.
	const createSpent = creating && failure?.kind === "request_id_reused";
	const createBlocked = creating && (!organisationsReady || createSpent);
	const loadFailure = uuid && draftQuery.isError ? classifyPrescriptionDraftFailure(draftQuery.error) : null;

	const prescriberDescription = creating ? (
		<>
			<Text strong>You — {sessionName}</Text>
			<br />
			<Text type="secondary" style={{ fontSize: 12 }}>
				Healthcare confirms your prescriber identity and current affiliation when you save. They are never taken from this form.
			</Text>
		</>
	) : draft && ownDraft ? (
		<>
			<Text strong>You — {sessionName}</Text>
			<br />
			<Text type="secondary" style={{ fontSize: 12 }}>
				Prescriber identity verified by Healthcare · reference {shortReference(draft.prescriber_provider_uuid)}
			</Text>
		</>
	) : (
		<Text type="warning">Another prescriber. Only the author of a draft may edit it.</Text>
	);

	const organisationField = creating || editing ? (
		organisationsReady ? (
			<Form.Item label="Prescribing organisation" name="organisation_uuid" rules={[{ required: true, message: "Select the organisation this prescription is made for." }]}>
				<Select
					placeholder="Select the prescribing organisation"
					options={[
						...organisationOptions.map((org) => ({ value: org.uuid, label: org.label })),
						...(editBase && !organisationOptions.some((org) => org.uuid === editBase.organisation_uuid)
							? [{ value: editBase.organisation_uuid, label: `${organisationLabel(editBase.organisation_uuid)} (current)` }]
							: []),
					]}
					disabled={saving}
					data-testid="prescriptions-organisation-select"
				/>
			</Form.Item>
		) : creating ? (
			<Alert
				type="warning"
				showIcon
				style={{ marginBottom: 16 }}
				data-testid="prescriptions-organisation-unavailable"
				message="No prescribing organisation can be selected"
				description={
					organisations.status === "ready"
						? "You have no organisation you can currently prescribe for, so a new draft cannot be saved. Ask your practice administrator to check your affiliation."
						: organisations.status === "loading"
							? "Loading the organisations you may prescribe for…"
							: "Healthcare does not yet provide the list of organisations you may prescribe for, so a new draft cannot be saved. Existing drafts can still be edited."
				}
			/>
		) : (
			<Form.Item label="Prescribing organisation" extra="The organisation cannot be changed until organisation selection is available.">
				<Text data-testid="prescriptions-organisation-current">{editBase ? organisationLabel(editBase.organisation_uuid) : "—"}</Text>
			</Form.Item>
		)
	) : null;

	const footer = creating || editing
		? [
				<Button key="cancel" onClick={creating ? requestClose : cancelEdit} disabled={saving}>
					Cancel
				</Button>,
				<Button key="save" type="primary" onClick={handleSave} loading={saving} disabled={createBlocked || (editing && !editBase)} data-testid="prescriptions-save-draft">
					Save draft
				</Button>,
			]
		: [
				<Button key="close" onClick={onClose}>
					Close
				</Button>,
				...(mayEdit
					? [
							<Button key="edit" type="primary" onClick={startEdit} data-testid="prescriptions-edit-draft">
								Edit draft
							</Button>,
						]
					: []),
			];

	const title = creating ? "New prescription draft" : editing ? "Edit prescription draft" : "Prescription draft";
	const particularColumns = screens.md ? 2 : 1;

	return (
		<Modal
			open
			title={title}
			onCancel={saving ? undefined : requestClose}
			maskClosable={false}
			footer={footer}
			width={screens.md ? 760 : "100%"}
			style={screens.md ? undefined : { top: 0, maxWidth: "100%", paddingBottom: 0 }}
			destroyOnClose
			data-testid="prescriptions-draft-modal"
		>
			<Alert
				type="info"
				showIcon
				style={{ marginBottom: 16 }}
				message={`${DRAFT_NOT_ISSUED_LABEL}. This has not been approved, signed, issued or sent.`}
				description="Saving stores a working draft in Healthcare only. Nothing is sent to a pharmacy, the patient or any eRx service."
			/>

			<div aria-live="polite" role="status" style={{ minHeight: statusText ? 20 : 0, marginBottom: statusText ? 8 : 0 }}>
				{statusText && <Text type="secondary">{statusText}</Text>}
			</div>

			{failure && (
				<Alert
					type={failure.kind === "ambiguous" || failure.kind === "stale_revision" || failure.kind === "request_id_reused" ? "warning" : "error"}
					showIcon
					style={{ marginBottom: 16 }}
					data-testid="prescriptions-save-failure"
					message={failure.message}
					action={
						failure.kind === "ambiguous" ? (
							<Button size="small" onClick={handleSave} loading={saving} data-testid="prescriptions-retry-save">
								Retry save
							</Button>
						) : failure.kind === "stale_revision" ? (
							<Button size="small" onClick={loadLatest} data-testid="prescriptions-load-latest">
								Load latest version
							</Button>
						) : undefined
					}
					description={
						failure.kind === "stale_revision" && failure.currentRevision
							? `The latest saved version is revision ${failure.currentRevision}. Loading it discards your unsaved changes.`
							: createSpent
								? "Close this form and open the saved draft from the list. Saving again here could create a duplicate."
								: undefined
					}
				/>
			)}

			{uuid && draftQuery.isLoading && <Text type="secondary">Loading draft…</Text>}
			{loadFailure && loadFailure.kind !== "stepup_declined" && <Alert type="error" showIcon message={loadFailure.message} data-testid="prescriptions-load-failure" />}
			{wrongPatient && (
				<Alert type="error" showIcon data-testid="prescriptions-wrong-patient" message="This draft does not belong to the open patient record, so it is not shown." />
			)}

			{(creating || (draft && !wrongPatient)) && (
				<Form form={form} layout="vertical" disabled={!(creating || editing) || saving} requiredMark>
					<Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
						<Descriptions.Item label="Status">
							{draft && !isEditableDraft(draft) ? (
								<StatusTag status="unknown" label={`Unrecognised status (${draft.status}) — read only`} />
							) : (
								<StatusTag status="draft" label={DRAFT_NOT_ISSUED_LABEL} />
							)}
						</Descriptions.Item>
						<Descriptions.Item label="Patient">{[patient.first_name, patient.last_name].filter(Boolean).join(" ") || "This patient"}</Descriptions.Item>
						<Descriptions.Item label="Prescriber">{prescriberDescription}</Descriptions.Item>
						{draft && !editing && <Descriptions.Item label="Prescribing organisation">{organisationLabel(draft.organisation_uuid)}</Descriptions.Item>}
						{draft && !editing && <Descriptions.Item label="Prescription type">{modalityLabel(draft.modality)}</Descriptions.Item>}
						{draft && (
							<Descriptions.Item label="Revision">
								{draft.revision} · last saved {formatInstant(draft.updated_at)}
							</Descriptions.Item>
						)}
					</Descriptions>

					{(creating || editing) && (
						<>
							{organisationField}
							<Form.Item label="Prescription type" name="modality" rules={[{ required: true, message: "Choose electronic or paper." }]}>
								<Radio.Group options={PRESCRIPTION_MODALITIES} optionType="button" disabled={saving} aria-label="Prescription type" data-testid="prescriptions-modality" />
							</Form.Item>
							<PrescriptionParticularsFields form={form} disabled={saving} />
						</>
					)}

					{draft && !editing && (
						<Descriptions column={particularColumns} size="small" title="Medicine and directions">
							{PRESCRIPTION_PARTICULAR_KEYS.map((key) => (
								<Descriptions.Item key={key} label={PRESCRIPTION_PARTICULAR_RULES[key].label} span={isLongParticular(key) ? particularColumns : 1}>
									{draft.particulars[key] ?? <Text type="secondary">Not entered</Text>}
								</Descriptions.Item>
							))}
						</Descriptions>
					)}
				</Form>
			)}

			{!creating && draft && !wrongPatient && !mayEdit && isEditableDraft(draft) && ownDraft && !canWritePrescriptionDrafts() && (
				<Space style={{ marginTop: 8 }}>
					<Text type="secondary">You can view this draft but your account cannot edit prescription drafts.</Text>
				</Space>
			)}
		</Modal>
	);
};

export default PrescriptionDraftModal;
